import { CommonModule, NgOptimizedImage } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { FlexLayoutModule } from '@angular/flex-layout';
import {
  FormBuilder,
  FormGroup,
  FormsModule,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subject } from 'rxjs';
import { BranchOfficeService } from 'src/app/main/pages/services/branch-office.service';
import { BranchOfficeSimpleJson } from 'src/app/main/pages/services/microservices/json/branch-office-simple.json';
import { BranchOfficeJson } from 'src/app/main/pages/services/microservices/json/branch-office.json';
import { invalidIP, NumberDirective } from 'src/app/shared';
import { FormValidator } from 'src/app/shared/utils/form-validator';
import { IPOnlyDirective } from 'src/app/shared/utils/ip-only.directive';

import { LoadingModule } from '../../../../../generals/loading/loading.module';
import { LoadingComponent } from 'src/app/main/pages/generals/loading/loading.component';

@Component({
  selector: 'app-branch-office-dialog',
  templateUrl: './branch-office-dialog.component.html',
  styleUrls: ['./branch-office-dialog.component.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    FlexLayoutModule,
    MatInputModule,
    MatButtonModule,
    MatDialogModule,
    MatTooltipModule,
    MatSelectModule,
    MatIconModule,
    NgOptimizedImage,
    NumberDirective,
    LoadingComponent,
    IPOnlyDirective,
  ],
})
export class BranchOfficeDialogComponent implements OnInit, OnDestroy {
  readonly dialogRef = inject(MatDialogRef<BranchOfficeDialogComponent>);
  private fb = inject(FormBuilder);
  private fv = inject(FormValidator);
  private _unsubscribeAll!: Subject<any>;
  private _snackBar = inject(MatSnackBar);
  private service = inject(BranchOfficeService);

  public data: {
    id: number;
    establishmentId: number;
    establishmentNumber: number;
  } = inject(MAT_DIALOG_DATA);

  txtBtn = 'REGISTRAR';
  isOwnDomain!: boolean;
  isLoading = false;
  showPassword = false;
  notification: any = {};
  form!: FormGroup;

  ngOnInit(): void {
    this._unsubscribeAll = new Subject<any>();
    this.formInit();
    this.load();
  }

  private load(): void {
    if (this.data.establishmentId > 0) {
      if (this.data.id > 0) {
        this.getBranchOffice();
      } else {
        this.getNextBranchOfficeNumber();
      }
    } else {
      this.form.disable();
    }
  }

  getBranchOffice(): void {
    this.service.get(this.data.id).subscribe({
      next: response => {
        this.patchBranchOffice(response.data);
        this.addZero(response.data.number);
        this.txtBtn = 'ACTUALIZAR';
      },
      error: () => {
        this.form.invalid;
      },
    });
  }

  private getNextBranchOfficeNumber() {
    this.service
      .getNextBranchOfficeNumber(this.data.establishmentId)
      .subscribe({
        next: response => {
          this.addZero(response.data.number);
        },
        error: () => {
          this.form.invalid;
        },
      });
  }

  submit(): void {
    this.isLoading = true;
    const data = this.generateJson();
    if (this.form.get('id')?.value > 0) {
      this.update(data);
    } else {
      this.save(data);
    }
  }

  save(data: BranchOfficeJson): void {
    this.service.save(data).subscribe({
      next: response => {
        this.dialogRef.close(this.getJsonResult(response.data.id));
        this.isLoading = false;
      },
    });
  }

  update(data: BranchOfficeJson): void {
    this.service.update(data).subscribe({
      next: response => {
        this.dialogRef.close(this.getJsonResult(response.data.id));
        this.isLoading = false;
      },
    });
  }

  isInvalidForm(): boolean {
    if (this.form.get('isOwnDomain')?.value === 'true') {
      return (
        this.form.controls['citadel'].invalid ||
        this.form.controls['host'].invalid ||
        this.form.controls['port'].invalid ||
        this.form.controls['user'].invalid ||
        this.form.controls['password'].invalid ||
        this.form.controls['from'].invalid
      );
    } else if (this.form.get('isOwnDomain')?.value === 'false') {
      return this.form.controls['citadel'].invalid;
    } else {
      return true;
    }
  }

  controlHasError(formControlName: string): boolean {
    return this.fv.fieldHasError(formControlName);
  }

  private formInit(): void {
    this.form = this.fb.group(
      {
        id: [0],
        establishmentNumber: [{ value: '', disabled: true }],
        citadel: ['', Validators.required],
        active: [''],
        isOwnDomain: ['', Validators.required],
        ownDomainId: [0],
        email: [{ value: 'facturacion@idempier.com', disabled: true }],
        host: ['', [Validators.required, invalidIP]],
        port: ['', Validators.required],
        user: ['', Validators.required],
        password: ['', Validators.required],
        from: ['', [Validators.required, Validators.email]],
      },
      { updateOn: 'blur' },
    );
    this.fieldNotificationInit();
  }

  private fieldNotificationInit(): void {
    const fields = {
      establishmentNumber: {
        required: 'Numero de establecimiento es requerido',
      },
      citadel: { required: 'Ciudadela es requerida' },
      host: { invalidIP: 'Host invalido' },
    };
    this.fv.setFormValidator(this.form, this.notification, fields);
  }

  private openSnack(message: string): void {
    this._snackBar.open(message, '', {
      verticalPosition: 'top',
      horizontalPosition: 'right',
      panelClass: ['snack-error'],
      duration: 5000,
    });
  }

  private generateJson(): BranchOfficeJson {
    let ownDomain = null;
    let defaultDomain = null;
    if (this.form.get('isOwnDomain')?.value === 'true') {
      const ownDomainId = this.form.get('ownDomainId')?.value;
      ownDomain = {
        id: ownDomainId,
        host: this.form.get('host')?.value,
        port: this.form.get('port')?.value,
        user: this.form.get('user')?.value,
        password: this.form.get('password')?.value,
        from: this.form.get('from')?.value,
      };
    } else {
      defaultDomain = {
        email: this.form.get('email')?.value,
      };
    }
    const json = {
      id: this.form.get('id')?.value,
      number: 0,
      establishmentId: this.data.establishmentId,
      citadel: this.form.get('citadel')?.value,
      ownDomain: ownDomain,
      defaultDomain,
      active: true,
    };
    return json;
  }

  private getJsonResult(id: number): BranchOfficeSimpleJson {
    const activeField = this.form.get('active')?.value;
    const active = activeField === '' ? true : activeField;
    return {
      id,
      number: Number(this.form.get('establishmentNumber')?.value),
      citadel: this.form.get('citadel')?.value,
      establishmentNumber: this.form.get('establishmentNumber')?.value,
      active,
      businessUnit: null,
    };
  }

  private patchBranchOffice(bo: BranchOfficeJson): void {
    if (bo.ownDomain !== null) {
      this.form.patchValue({ ownDomainId: bo.ownDomain.id });
      this.form.patchValue(bo.ownDomain);
      this.form.patchValue({ isOwnDomain: 'true' });
    } else if (bo.defaultDomain !== null) {
      this.form.patchValue(bo.defaultDomain);
      this.form.patchValue({ isOwnDomain: 'false' });
    }
    this.form.patchValue(bo);
  }

  private addZero(nextNumber: number): void {
    let number = nextNumber < 100 ? '0' : '';
    if (nextNumber < 10) {
      number += '0';
    }
    number += nextNumber;
    this.form.patchValue({ establishmentNumber: number });
  }

  ngOnDestroy(): void {
    this._unsubscribeAll.next(void 0);
    this._unsubscribeAll.complete();
  }
}
